import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLpgCheckerConfig, updateLpgCheckerConfig, fetchLpgBills, fetchLpgBillsText, getLpgCheckerSessions, getLpgCheckerSession, downloadLpgSession, downloadLpgProxy } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MagnifyingGlass, FileXls, Gear, Lightning, CheckCircle, XCircle, UploadSimple, DownloadSimple, Clock, Desktop } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function LpgCheckerPage() {
  const { user } = useAuth();
  const [operator, setOperator] = useState('indane');
  const [file, setFile] = useState(null);
  const [textIds, setTextIds] = useState('');
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'excel'
  const [fetching, setFetching] = useState(false);
  const [results, setResults] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [progress, setProgress] = useState({ fetched: 0, total: 0 });
  const [sessions, setSessions] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState({ auth_token: '', customer_hash: '', sign_in_mobile: '' });
  const [configSaving, setConfigSaving] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';

  const fetchSessions = useCallback(async () => {
    try {
      const res = await getLpgCheckerSessions();
      setSessions(res.data);
    } catch (err) { console.error('LPG config load failed:', err?.message); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const loadConfig = async () => {
    try {
      const res = await getLpgCheckerConfig();
      setConfig(res.data);
    } catch (err) { toast.error('Config load failed'); }
  };

  const handleConfigSave = async () => {
    setConfigSaving(true);
    try {
      await updateLpgCheckerConfig(config);
      toast.success('Config saved');
      setConfigOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setConfigSaving(false); }
  };

  const handleFetch = async () => {
    if (inputMode === 'excel' && !file) { toast.error('Excel file select karo'); return; }
    if (inputMode === 'text' && !textIds.trim()) { toast.error('LPG IDs paste karo'); return; }
    setFetching(true);
    setResults(null);
    setProgress({ fetched: 0, total: 0 });
    try {
      let res;
      if (inputMode === 'excel') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('operator', operator);
        res = await fetchLpgBills(fd);
      } else {
        res = await fetchLpgBillsText({ lpg_ids_text: textIds, operator });
      }
      const sessionId = res.data.session_id;
      setActiveSessionId(sessionId);
      setProgress({ fetched: 0, total: res.data.total });
      toast.success(`${res.data.total} IDs ka fetch start hua!`);
      pollSession(sessionId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fetch failed — check config');
      setFetching(false);
    }
  };

  const pollSession = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 2500; // ~2000 IDs * 1s + buffer
    const poll = async () => {
      try {
        const res = await getLpgCheckerSession(sessionId);
        const data = res.data;
        setProgress({ fetched: data.fetched, total: data.total_ids });
        if (data.status === 'completed') {
          setResults({
            session_id: sessionId,
            operator: data.operator_name,
            total: data.total_ids,
            fetched: data.fetched,
            errors: data.errors,
            results: data.results,
          });
          setFetching(false);
          setActiveSessionId(null);
          fetchSessions();
          toast.success(`${data.fetched} bills fetched! ${data.errors} errors.`);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        } else {
          setFetching(false);
          toast.error('Timeout — session still running, check sessions below');
        }
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 5000);
        else setFetching(false);
      }
    };
    setTimeout(poll, 3000);
  };

  const handleDownload = async (sessionId) => {
    try {
      const res = await downloadLpgSession(sessionId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lpg_bill_data.xlsx`;
      a.click();
    } catch (err) { toast.error('Download failed'); }
  };

  const handleDownloadProxy = async () => {
    try {
      const res = await downloadLpgProxy();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lpg_proxy.py';
      a.click();
      toast.success('Proxy script downloaded! Laptop pe run karo: python lpg_proxy.py');
    } catch (err) { toast.error('Download failed'); }
  };

  const formatCurrency = (v) => {
    if (!v || v === 'ERROR') return '—';
    const num = parseFloat(v);
    return isNaN(num) ? v : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num / 100);
  };

  if (!isAdmin) {
    return <div className="p-6 text-center text-red-600 font-medium">Access denied. LPG Checker sirf Admin/Owner/Assistant ke liye hai.</div>;
  }

  return (
    <div className="space-y-5" data-testid="lpg-checker-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MagnifyingGlass size={24} className="text-indigo-600" /> LPG Bill Checker
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">TataNeu se LPG Bill fetch karo — Excel upload karo, bills auto-fetch honge</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 self-start">
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={handleDownloadProxy} data-testid="lpg-proxy-btn">
              <Desktop size={14} /> Proxy Script
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => { loadConfig(); setConfigOpen(true); }} data-testid="lpg-config-btn">
              <Gear size={14} /> Config
            </Button>
          </div>
        )}
      </div>

      {/* Fetch Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-sm font-medium">Operator</Label>
            <Select value={operator} onValueChange={setOperator}>
              <SelectTrigger className="mt-1" data-testid="lpg-operator-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="indane">Indane Gas (Indian Oil)</SelectItem>
                <SelectItem value="bpcl">Bharat Gas (BPCL)</SelectItem>
                <SelectItem value="hp">HP Gas (HPCL)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            {/* Input Mode Toggle */}
            <div className="flex gap-1 mb-1.5">
              <button onClick={() => setInputMode('text')} className={`text-xs px-3 py-1 rounded-t border-b-2 font-medium transition-colors ${inputMode === 'text' ? 'border-indigo-600 text-indigo-600 bg-indigo-50' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid="mode-text">
                Paste IDs
              </button>
              <button onClick={() => setInputMode('excel')} className={`text-xs px-3 py-1 rounded-t border-b-2 font-medium transition-colors ${inputMode === 'excel' ? 'border-indigo-600 text-indigo-600 bg-indigo-50' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid="mode-excel">
                Excel Upload
              </button>
            </div>
            {inputMode === 'text' ? (
              <textarea
                value={textIds}
                onChange={e => setTextIds(e.target.value)}
                placeholder={"Line-by-line LPG IDs paste karo:\n5000123456\n5000654321\n9876543210"}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono resize-y focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="lpg-text-input"
              />
            ) : (
              <label className="flex items-center gap-2 cursor-pointer border border-gray-300 rounded-md px-3 h-10 hover:bg-gray-50 text-sm text-gray-600">
                <UploadSimple size={16} className="text-blue-600" />
                {file ? file.name : 'Choose .xlsx file'}
                <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="lpg-file-input" />
              </label>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700 h-10 gap-2 px-8" onClick={handleFetch} disabled={fetching || (inputMode === 'excel' ? !file : !textIds.trim())} data-testid="lpg-fetch-btn">
            {fetching ? (
              <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Fetching...</>
            ) : (
              <><Lightning size={16} weight="fill" /> Fetch Bills</>
            )}
          </Button>
          {inputMode === 'text' && textIds.trim() && (
            <span className="text-xs text-gray-400">{textIds.trim().split('\n').filter(l => l.trim()).length} IDs</span>
          )}
        </div>
        {fetching && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-indigo-700 font-medium">
                  {progress.fetched === 0 ? 'Laptop proxy se connect ho raha hai...' : 'Bills fetch ho rahe hain...'}
                </p>
                <p className="text-xs text-indigo-500">{progress.fetched} / {progress.total} done</p>
                {progress.total > 0 && (
                  <div className="mt-2 bg-indigo-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-indigo-600 h-full rounded-full transition-all duration-500" style={{ width: `${Math.round((progress.fetched / progress.total) * 100)}%` }} />
                  </div>
                )}
                {progress.fetched === 0 && (
                  <p className="text-[10px] text-indigo-400 mt-1">Make sure LPG Proxy script laptop pe chal raha hai</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-bold text-gray-700">Results — {results.operator}</h2>
              <p className="text-xs text-gray-500">Total: {results.total} | Fetched: {results.fetched} | Errors: {results.errors}</p>
            </div>
            {results.session_id && (
              <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={() => handleDownload(results.session_id)} data-testid="lpg-download-results">
                <FileXls size={14} className="text-green-600" /> Download Excel
              </Button>
            )}
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
            {results.results?.map((r, i) => (
              <div key={i} className={`p-3 ${r.error ? 'bg-red-50/50' : ''}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-mono font-bold text-gray-900">{r.lpg_id}</p>
                    <p className="text-xs text-gray-600">{r.customer_name || '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{formatCurrency(r.bill_amount)}</p>
                    {r.error ? <XCircle size={14} className="text-red-500 inline" /> : <CheckCircle size={14} className="text-green-500 inline" />}
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{r.bill_status} {r.bill_id && `| ${r.bill_id}`}</p>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 sticky top-0">
                  <TableHead className="text-xs">#</TableHead>
                  <TableHead className="text-xs">LPG ID</TableHead>
                  <TableHead className="text-xs">Customer Name</TableHead>
                  <TableHead className="text-xs text-right">Bill Amount</TableHead>
                  <TableHead className="text-xs">Bill Status</TableHead>
                  <TableHead className="text-xs">Bill ID</TableHead>
                  <TableHead className="text-xs text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.results?.map((r, i) => (
                  <TableRow key={i} className={r.error ? 'bg-red-50/50' : ''}>
                    <TableCell className="text-xs text-gray-400">{i + 1}</TableCell>
                    <TableCell className="font-mono font-medium text-sm">{r.lpg_id}</TableCell>
                    <TableCell className="text-sm">{r.customer_name || '—'}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-sm">{formatCurrency(r.bill_amount)}</TableCell>
                    <TableCell className="text-xs text-gray-600">{r.bill_status}</TableCell>
                    <TableCell className="text-xs font-mono text-gray-500">{r.bill_id || '—'}</TableCell>
                    <TableCell className="text-center">
                      {r.error ? <XCircle size={16} className="text-red-500 mx-auto" /> : <CheckCircle size={16} className="text-green-500 mx-auto" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Previous Sessions */}
      {sessions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2"><Clock size={16} /> Previous Sessions</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {sessions.map(s => (
              <div key={s.id} className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{s.operator_name} — {s.total_ids} IDs</p>
                    {s.status === 'running' ? (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded animate-pulse">Running</span>
                    ) : (
                      <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded">Done</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">By {s.fetched_by} | {formatDate(s.created_at)} | {s.fetched} fetched, {s.errors} errors</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="gap-1 h-7 text-xs shrink-0" onClick={async () => {
                    try {
                      const res = await getLpgCheckerSession(s.id);
                      setResults({ session_id: s.id, operator: s.operator_name, total: s.total_ids, fetched: res.data.fetched, errors: res.data.errors, results: res.data.results });
                    } catch (err) { toast.error('Failed to load session'); }
                  }} data-testid={`view-session-${s.id}`}>
                    <MagnifyingGlass size={12} /> View
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1 h-7 text-xs shrink-0" onClick={() => handleDownload(s.id)} data-testid={`download-session-${s.id}`}>
                    <DownloadSimple size={12} /> Excel
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>TataNeu Config</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">AUTH_TOKEN expire hota hai — regularly update karo TataNeu se</p>
            <div>
              <Label className="text-sm">AUTH_TOKEN</Label>
              <Input value={config.auth_token} onChange={e => setConfig(p => ({ ...p, auth_token: e.target.value }))} placeholder="12ebdb0e-71d8-..." className="font-mono text-xs mt-1" data-testid="config-auth-token" />
            </div>
            <div>
              <Label className="text-sm">Customer Hash</Label>
              <Input value={config.customer_hash} onChange={e => setConfig(p => ({ ...p, customer_hash: e.target.value }))} placeholder="781a4c75445188..." className="font-mono text-xs mt-1" data-testid="config-customer-hash" />
            </div>
            <div>
              <Label className="text-sm">Sign In Mobile</Label>
              <Input value={config.sign_in_mobile} onChange={e => setConfig(p => ({ ...p, sign_in_mobile: e.target.value }))} placeholder="7854966912" className="font-mono text-xs mt-1" data-testid="config-sign-mobile" />
            </div>
            {config.updated_by && (
              <p className="text-[10px] text-gray-400">Last updated by {config.updated_by} {config.updated_at && `at ${formatDate(config.updated_at)}`}</p>
            )}
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={handleConfigSave} disabled={configSaving} data-testid="config-save-btn">
              {configSaving ? 'Saving...' : 'Save Config'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
